import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import {
  X, Smartphone, Monitor, ChefHat, ShoppingCart, Send,
  Plus, Minus, Sparkles, UtensilsCrossed
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerPreviewModal({ open, onClose }) {
  const { token, activeBranch } = useAuth();
  const { isAr } = useLanguage();
  const [siteData, setSiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('mobile');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [cart, setCart] = useState({});
  const [showMenu, setShowMenu] = useState(false);
  const [sessionId] = useState(() => `preview-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const chatEndRef = useRef(null);
  const [suggestedReplies, setSuggestedReplies] = useState([]);

  const l = useCallback((ar, en) => isAr ? ar : en, [isAr]);

  useEffect(() => {
    if (open && activeBranch && token) {
      fetchSiteData();
      fetchMenu();
    }
  }, [open, activeBranch, token]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchSiteData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/conversational/status?branch_id=${activeBranch.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.exists && data.site) {
        setSiteData(data.site);
        const cfg = data.site.config || {};
        const welcomeMsg = cfg.welcome_message || `أهلاً بك في ${cfg.restaurant_name || activeBranch.name}! كيف أقدر أساعدك؟`;
        setMessages([{ role: 'bot', text: welcomeMsg, ts: new Date() }]);
      }
    } catch { }
    setLoading(false);
  };

  const fetchMenu = async () => {
    try {
      const { data } = await axios.get(`${API}/conversational/menu/${activeBranch.id}`);
      setMenuItems(data.items || []);
    } catch { }
  };

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || sending || !siteData) return;
    setInput('');
    setSuggestedReplies([]);
    setMessages(prev => [...prev, { role: 'user', text: msg, ts: new Date() }]);
    setSending(true);

    try {
      const { data } = await axios.post(`${API}/conversational/chat`, {
        site_id: siteData.id,
        message: msg,
        session_id: sessionId,
        cart: Object.entries(cart).map(([id, qty]) => {
          const item = menuItems.find(m => m.id === id);
          return item ? { item_id: id, name: item.name_ar, quantity: qty, price: item.price } : null;
        }).filter(Boolean),
      });

      const botMsg = { role: 'bot', text: data.reply || data.response || '', ts: new Date() };
      if (data.rich_cards) botMsg.rich_cards = data.rich_cards;
      setMessages(prev => [...prev, botMsg]);

      if (data.suggested_replies?.length) {
        setSuggestedReplies(data.suggested_replies);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'bot', text: 'عذراً، حدث خطأ. حاول مرة أخرى.', ts: new Date() }]);
    }
    setSending(false);
  };

  const addToCart = (itemId) => setCart(prev => ({ ...prev, [itemId]: (prev[itemId] || 0) + 1 }));
  const removeFromCart = (itemId) => setCart(prev => {
    const next = { ...prev };
    if (next[itemId] > 1) next[itemId]--;
    else delete next[itemId];
    return next;
  });

  const totalItems = Object.values(cart).reduce((a, b) => a + b, 0);
  const totalPrice = Object.entries(cart).reduce((sum, [id, qty]) => {
    const item = menuItems.find(m => m.id === id);
    return sum + (item ? item.price * qty : 0);
  }, 0);

  const config = siteData?.config || {};
  const primaryColor = config.colors?.primary || '#D97706';
  const restaurantName = config.restaurant_name || activeBranch?.name || '';
  const logoUrl = config.logo_url || '';

  if (!open) return null;

  const phoneWidth = viewMode === 'mobile' ? 390 : 768;
  const phoneHeight = viewMode === 'mobile' ? 720 : 600;

  return (
    <div
      data-testid="customer-preview-modal"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Tajawal', sans-serif",
        direction: 'rtl',
        animation: 'previewFadeIn 0.3s ease-out',
      }}
    >
      {/* Top Bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        padding: '12px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            padding: '5px 14px', borderRadius: 20,
            background: 'rgba(217,119,6,0.15)',
            border: '1px solid rgba(217,119,6,0.3)',
            color: '#F59E0B', fontSize: 12, fontWeight: 600,
          }}>
            {l('وضع المعاينة', 'Preview Mode')}
          </div>
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
            {l('هذا ما يراه عملاؤك', 'This is what your customers see')}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* View Mode Toggle */}
          <div style={{
            display: 'flex', borderRadius: 10,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden',
          }}>
            {[
              { id: 'mobile', icon: Smartphone, label: l('جوال', 'Mobile') },
              { id: 'tablet', icon: Monitor, label: l('تابلت', 'Tablet') },
            ].map(v => (
              <button
                key={v.id}
                data-testid={`preview-mode-${v.id}`}
                onClick={() => setViewMode(v.id)}
                style={{
                  padding: '6px 14px',
                  display: 'flex', alignItems: 'center', gap: 5,
                  fontSize: 11, fontWeight: 500,
                  color: viewMode === v.id ? '#F59E0B' : 'rgba(255,255,255,0.35)',
                  background: viewMode === v.id ? 'rgba(217,119,6,0.1)' : 'transparent',
                  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                <v.icon style={{ width: 14, height: 14 }} />
                {v.label}
              </button>
            ))}
          </div>

          <button
            data-testid="preview-close-btn"
            onClick={onClose}
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </div>

      {/* Phone Frame */}
      <div style={{
        width: phoneWidth,
        height: phoneHeight,
        maxWidth: 'calc(100vw - 40px)',
        maxHeight: 'calc(100vh - 100px)',
        borderRadius: viewMode === 'mobile' ? 40 : 20,
        border: viewMode === 'mobile' ? '8px solid #2A2A2E' : '6px solid #2A2A2E',
        overflow: 'hidden',
        background: config.colors?.bg || '#FFF9F0',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 40px 100px rgba(0,0,0,0.4)',
        transition: 'width 0.3s, height 0.3s, border-radius 0.3s',
      }}>
        {/* Notch (mobile only) */}
        {viewMode === 'mobile' && (
          <div style={{
            height: 28, background: '#2A2A2E',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ width: 80, height: 6, borderRadius: 3, background: '#444' }} />
          </div>
        )}

        {/* Chat Header */}
        <div style={{
          padding: '10px 16px',
          background: '#fff',
          borderBottom: `1px solid ${primaryColor}15`,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          {logoUrl ? (
            <img
              src={logoUrl.startsWith('/') ? `${API_URL}${logoUrl}` : logoUrl}
              alt="logo"
              style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover' }}
            />
          ) : (
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}CC)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ChefHat style={{ width: 18, height: 18, color: '#fff' }} />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: config.colors?.text || '#292524', margin: 0 }}>{restaurantName}</p>
            <p style={{ fontSize: 11, color: config.colors?.text_light || '#78716C', margin: 0 }}>{config.tagline || 'أشهى المأكولات'}</p>
          </div>
          {totalItems > 0 && (
            <button
              onClick={() => setShowMenu(!showMenu)}
              style={{
                position: 'relative', width: 36, height: 36, borderRadius: 10,
                background: `${primaryColor}10`, border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <ShoppingCart style={{ width: 18, height: 18, color: primaryColor }} />
              <span style={{
                position: 'absolute', top: -4, right: -4,
                width: 18, height: 18, borderRadius: '50%',
                background: primaryColor, color: '#fff',
                fontSize: 10, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{totalItems}</span>
            </button>
          )}
        </div>

        {/* Messages */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: 12,
          display: 'flex', flexDirection: 'column', gap: 8,
          background: config.colors?.bg || '#FFF9F0',
        }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <Sparkles style={{ width: 24, height: 24, color: primaryColor, animation: 'spin 1s linear infinite' }} />
            </div>
          ) : messages.map((msg, i) => (
            <div key={i} style={{
              alignSelf: msg.role === 'user' ? 'flex-start' : 'flex-end',
              maxWidth: '85%',
            }}>
              <div style={{
                padding: '10px 14px',
                borderRadius: msg.role === 'user' ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
                background: msg.role === 'user' ? `linear-gradient(135deg, ${primaryColor}, ${primaryColor}DD)` : '#fff',
                color: msg.role === 'user' ? '#fff' : (config.colors?.text || '#292524'),
                fontSize: 13, lineHeight: 1.6,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                whiteSpace: 'pre-wrap',
              }}>
                {msg.text}
              </div>
              {msg.rich_cards && msg.rich_cards.map((card, ci) => (
                <div key={ci} style={{
                  marginTop: 6, padding: '8px 12px', borderRadius: 12,
                  background: '#fff', border: `1px solid ${primaryColor}15`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                  {card.type === 'menu_item' && card.data && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {card.data.image_url && <img src={card.data.image_url} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />}
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 12, fontWeight: 600, margin: 0, color: config.colors?.text || '#292524' }}>{card.data.name_ar || card.data.name}</p>
                        <p style={{ fontSize: 11, color: primaryColor, fontWeight: 600, margin: 0 }}>{card.data.price} {l('ر.س', 'SAR')}</p>
                      </div>
                      <button onClick={() => addToCart(card.data.id || card.data.item_id)} style={{
                        width: 26, height: 26, borderRadius: 8,
                        background: `${primaryColor}15`, border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}><Plus style={{ width: 12, height: 12, color: primaryColor }} /></button>
                    </div>
                  )}
                  {card.type !== 'menu_item' && card.data && (
                    <p style={{ fontSize: 12, color: config.colors?.text || '#292524', margin: 0 }}>{card.data.title || card.data.text || JSON.stringify(card.data)}</p>
                  )}
                </div>
              ))}
              <p style={{
                fontSize: 10, color: config.colors?.text_light || '#A8A29E',
                marginTop: 3,
                textAlign: msg.role === 'user' ? 'left' : 'right',
              }}>
                {msg.ts ? new Date(msg.ts).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' }) : ''}
              </p>
            </div>
          ))}
          {sending && (
            <div style={{
              alignSelf: 'flex-end', padding: '10px 16px',
              borderRadius: '16px 4px 16px 16px',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {[0, 1, 2].map(d => (
                  <div key={d} style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: primaryColor,
                    animation: `dotPulse 0.8s ease-in-out ${d * 0.15}s infinite`,
                  }} />
                ))}
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Suggested Replies */}
        {suggestedReplies.length > 0 && (
          <div style={{
            padding: '6px 12px',
            display: 'flex', gap: 6, flexWrap: 'wrap',
            borderTop: '1px solid rgba(0,0,0,0.04)',
            background: '#fff',
          }}>
            {suggestedReplies.map((r, i) => (
              <button
                key={i}
                data-testid={`preview-suggestion-${i}`}
                onClick={() => sendMessage(r)}
                style={{
                  padding: '6px 12px', borderRadius: 16,
                  border: `1px solid ${primaryColor}30`,
                  background: `${primaryColor}08`,
                  color: primaryColor,
                  fontSize: 11, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >{r}</button>
            ))}
          </div>
        )}

        {/* Cart Summary */}
        {totalItems > 0 && (
          <div style={{
            padding: '8px 12px',
            background: primaryColor,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>
              {totalItems} {l('أصناف', 'items')} - {totalPrice.toFixed(0)} {l('ر.س', 'SAR')}
            </span>
            <button
              onClick={() => sendMessage('أريد إتمام الطلب')}
              style={{
                padding: '5px 14px', borderRadius: 12,
                background: '#fff', color: primaryColor,
                fontSize: 11, fontWeight: 700,
                border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {l('اطلب الآن', 'Order Now')}
            </button>
          </div>
        )}

        {/* Input */}
        <div style={{
          padding: '8px 12px',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          background: '#fff',
          display: 'flex', gap: 8, alignItems: 'center',
        }}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            style={{
              width: 34, height: 34, borderRadius: 10,
              background: `${primaryColor}10`,
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <UtensilsCrossed style={{ width: 16, height: 16, color: primaryColor }} />
          </button>
          <input
            data-testid="preview-chat-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder={l('اكتب رسالتك...', 'Type your message...')}
            style={{
              flex: 1, padding: '8px 14px', borderRadius: 20,
              border: '1px solid rgba(0,0,0,0.08)',
              background: '#FAFAF9', fontSize: 13, outline: 'none',
              fontFamily: 'inherit', color: config.colors?.text || '#292524',
            }}
          />
          <button
            data-testid="preview-send-btn"
            onClick={() => sendMessage()}
            disabled={!input.trim() || sending}
            style={{
              width: 34, height: 34, borderRadius: '50%',
              background: input.trim() ? primaryColor : '#E7E5E4',
              border: 'none', cursor: input.trim() ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s',
            }}
          >
            <Send style={{ width: 15, height: 15, color: '#fff', transform: 'scaleX(-1)' }} />
          </button>
        </div>

        {/* Menu Drawer */}
        {showMenu && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: '60%', background: '#fff',
            borderTopLeftRadius: 20, borderTopRightRadius: 20,
            boxShadow: '0 -8px 30px rgba(0,0,0,0.1)',
            display: 'flex', flexDirection: 'column',
            zIndex: 5,
            animation: 'menuSlideUp 0.3s ease-out',
          }}>
            <div style={{
              padding: '12px 16px',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: config.colors?.text || '#292524' }}>
                {l('المنيو', 'Menu')}
              </span>
              <button
                onClick={() => setShowMenu(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#78716C' }}
              >
                <X style={{ width: 18, height: 18 }} />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
              {menuItems.map(item => (
                <div key={item.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 0',
                  borderBottom: '1px solid rgba(0,0,0,0.04)',
                }}>
                  {item.image_url && (
                    <img src={item.image_url} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover' }} />
                  )}
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: config.colors?.text || '#292524', margin: 0 }}>{item.name_ar}</p>
                    <p style={{ fontSize: 11, color: primaryColor, fontWeight: 600, margin: 0 }}>{item.price} {l('ر.س', 'SAR')}</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {cart[item.id] ? (
                      <>
                        <button onClick={() => removeFromCart(item.id)} style={{
                          width: 26, height: 26, borderRadius: 8,
                          background: '#FEE2E2', border: 'none', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Minus style={{ width: 12, height: 12, color: '#EF4444' }} />
                        </button>
                        <span style={{ fontSize: 13, fontWeight: 700, minWidth: 16, textAlign: 'center' }}>{cart[item.id]}</span>
                      </>
                    ) : null}
                    <button
                      data-testid={`preview-add-${item.id}`}
                      onClick={() => addToCart(item.id)}
                      style={{
                        width: 26, height: 26, borderRadius: 8,
                        background: `${primaryColor}15`, border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <Plus style={{ width: 12, height: 12, color: primaryColor }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes previewFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes dotPulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        @keyframes menuSlideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
